/**
 * Detailer v6: header tone phasing.
 *
 * The header is permanently transparent, so this module decides what the page
 * renders behind it and flips `data-header-tone` on <header-component>. The
 * header stylesheet maps that attribute to the chrome colors (nav links, icons,
 * currentColor SVG logo).
 *
 * CONTRACT: other modules style against this, do not change it:
 *   data-header-tone="on-dark"   backdrop is dark  -> LIGHT chrome (white)
 *   data-header-tone="on-light"  backdrop is light -> DARK  chrome (ink)
 *
 * ------------------------------------------------------------------------
 * WHY v5 EXISTED: two measured failures, both fatal, both designed out.
 * ------------------------------------------------------------------------
 *
 * 1. THE SCROLL LISTENER WAS ON THE WRONG OBJECT.
 *    assets/base.css:
 *
 *      @media (min-width: 990px) {
 *        html:has(.page-wrapper), html:has(.page-wrapper) body { overflow: hidden; }
 *        .page-wrapper { height: 100dvh; overflow-y: auto; }
 *      }
 *
 *    On desktop the document does not scroll: `div.page-wrapper` does.
 *    `window.scrollY` stays 0 while `page-wrapper.scrollTop` reaches 1400, so
 *    `window.addEventListener('scroll', …)` never fired and the tone never
 *    re-evaluated after first paint. Below 990px the window scrolls normally,
 *    so BOTH have to be handled, and the resolution has to be re-run whenever
 *    the viewport crosses that breakpoint. See resolveScrollers().
 *
 * 2. PIXEL SAMPLING KEPT LEAVING THE TONE LATCHED.
 *    Measured at scrollTop 1400, over the white "Featured products" section:
 *    `elementsFromPoint` returned div.resource-list, div.section,
 *    div.shopify-section, main.content-for-layout, every one of them with
 *    `backgroundColor: rgba(0, 0, 0, 0)`. That is not a bug in the theme, it is
 *    the theme's design (assets/base.css):
 *
 *      .shopify-section:not(.header-section) :is(.section, .cart-summary) { background: transparent; }
 *      .shopify-section:not(.header-section) .section-background {
 *        position: absolute; inset: 0;
 *        z-index: var(--layer-section-background);   // -2
 *        background-color: var(--color-background);
 *      }
 *
 *    The section surface is painted by a separate, negatively-stacked sibling.
 *    The old sampler walked the hit stack looking for alpha > 0, found nothing,
 *    hit its "media pending" branch and RETURNED EARLY: leaving the previous
 *    tone in place forever. A white logo sat on a white background.
 *
 *    Canvas sampling of arbitrary photography has now failed four ways: CORS
 *    taint, decode timing, overlay compositing, and the latched pending state
 *    above. v5 abandoned it completely. There is no <canvas> in this file and
 *    no getImageData anywhere.
 *
 * ------------------------------------------------------------------------
 * WHY v6 EXISTS: the tone blinked black on every slide change.
 * ------------------------------------------------------------------------
 *
 * Reported: "Everytime the slide changes, the header, logo, and action menu
 * icons go to black, then white again."
 *
 * MEASURED CAUSE. Mid-transition the hero has TWO slides straddling the sample
 * point, and select() in assets/slideshow.js has already stamped
 * `aria-hidden="false"` on the incoming one. Both therefore count as rendered,
 * but each is only partly over the track, so visibleRect() clips both below
 * FULL_BLEED_RATIO and heroBackdropTone() declines. The pass fell through to
 * surfaceTone(), whose stack walk found
 *
 *      slideshow-container { background-color: var(--color-background); }
 *                                    (snippets/slideshow-styles.liquid)
 *
 *, an OPAQUE #FFFFFF that sits BEHIND the slide media it was standing in for.
 * on-light for a handful of frames, then back to on-dark once the new slide
 * snapped: a black/white blink on every slide change.
 *
 * v6 answers it three ways, listed in the order they cut in:
 *
 * 1. SKIP TRANSITION FRAMES. assets/slideshow.js stamps `transitioning` on
 *    <slideshow-component> from Scroller's onScrollStart and clears it from
 *    onScrollEnd: which is debounced 50ms past the last scroll event, and
 *    fires for instant / reduced-motion jumps too, because Scroller's
 *    #handleScroll raises onScrollInit BEFORE it consults its `ignore` flag.
 *    That one attribute spans the whole slide change. `dragging`, and the
 *    two-rAF `refreshing-timeline` flag raised after the wraparound DOM
 *    reorder, cover the rest; the empty <slideshow-slide> placeholder that the
 *    same reorder parks in the target slide's DOM slot covers the microtask gap
 *    between them. While any of those holds at the sample point, no measurement
 *    is taken and the tone is left exactly as it is. MAX_SKIP_MS is the escape
 *    hatch: a signal that somehow never clears cannot freeze the chrome longer
 *    than that.
 *
 * 2. THE CONTAINER IS NOT A BACKDROP. When a SLIDE's own backdrop media covers
 *    the sample point, surfaceTone() refuses to read `slideshow-container` /
 *    `slideshow-slides` / `slideshow-component` backgrounds at all, and
 *    refuses the section token too, since on this page it resolves to the same
 *    white. It returns null, meaning "no trustworthy answer here", rather than
 *    a confidently wrong one. See slideBackdropCoversPoint() for why that
 *    cannot strand anything.
 *
 * 3. HYSTERESIS. A tone that differs from what is already on screen is never
 *    written off a single sample. It has to be read CONFIRMATIONS_TO_* times
 *    running AND survive SETTLE_MS_TO_* before it is committed; a sample that
 *    agrees with the current attribute stays a cheap no-op. Moving to dark
 *    chrome (on-light) is held to the stricter count and the longer settle,
 *    because white chrome stays legible over a photograph and black ink does
 *    not.
 *
 * AND IT STILL CANNOT LATCH. `null` now means "keep the current tone" instead
 * of "assume light", but only once a tone exists at all. The first definite
 * reading of the session is written immediately (a 200ms settle at first paint
 * would read as exactly the flash this file removes), a missing tone is seeded
 * from pageTone(), and while a change is pending the module polls itself every
 * PENDING_POLL_MS so the settle window closes even when the user has stopped
 * scrolling and no further events arrive. Scrolling off the hero into the white
 * products section therefore commits on-light in roughly SETTLE_MS_TO_LIGHT,
 * not on the 800ms backstop.
 *
 * ------------------------------------------------------------------------
 * THE ALGORITHM: four steps, and step 4 always answers.
 * ------------------------------------------------------------------------
 *
 * a. HINT WINS OUTRIGHT. `data-header-tone-hint="dark"|"light"` on the section,
 *    or on any ancestor of the sample point, is returned as-is. This is the
 *    escape hatch: no measurement, no argument. Nothing in the theme emits it
 *    today, it exists so a per-section or per-slide setting can be wired up
 *    later without touching this file.
 *
 * b. FULL-BLEED HERO MEDIA reads as dark. If a slideshow slide image, hero
 *    media grid, background-media image or background video covers the sample
 *    point and spans ~the whole section width, the backdrop is photography.
 *    Its overlays are composited over an assumed mid-grey photo, no pixels are
 *    read, and the result decides. See heroBackdropTone() for the numbers.
 *
 * c. OTHERWISE THE SECTION'S OWN SURFACE COLOR. Any painted background in the
 *    hit stack is composited first; when they are all transparent (the common
 *    case, per (2) above) the resolved `--color-background` custom property is
 *    read instead. Horizon publishes it on every `.color-custom-*` scope via
 *    snippets/contrast-override.liquid and it inherits down, so reading it off
 *    the deepest element under the header yields that section's surface -
 *    #FFFFFF for the product grids, #101010 for the Academy panel: and falls
 *    back to the `:root` page background for sections that set none.
 *
 * d. NEVER LATCH. Every evaluation ends in commitTone(). Missing data walks
 *    down to the page background whenever no tone has ever been written, a
 *    throw inside the pass still seeds pageTone() from the catch block, and the
 *    safety interval keeps re-running the pass regardless.
 *
 * Motion: nothing here animates. The 200ms tone crossfade lives in
 * sections/header.liquid and is already disabled under
 * `prefers-reduced-motion: reduce`. This module only ever writes one attribute,
 * and only when its value actually changes.
 */

const TONE_DARK = 'on-dark';
const TONE_LIGHT = 'on-light';

const HEADER_ID = 'header-component';
const TONE_ATTRIBUTE = 'data-header-tone';
const HINT_ATTRIBUTE = 'data-header-tone-hint';

/* Sentinel: the slideshow under the sample point is mid-transition, so this
   frame carries no usable reading. Distinct from null ("measured, but the
   answer is not trustworthy") only so the skip can be time-capped. */
const UNSETTLED = 'unsettled';

/* White text out-contrasts black when backdrop luminance < ~0.179 (WCAG). */
const LUMINANCE_FLIP_POINT = 0.179;

/**
 * Stand-in luminance for "there is a photograph here".
 *
 * sRGB mid-grey: channel value 0.5, i.e. halfway up the encoded ramp, has a
 * RELATIVE luminance of 0.214, not 0.5. The 0.5 is the value before the sRGB
 * transfer function; feeding it in raw would be a 2.3x overestimate of how
 * bright a neutral photo is, and would flip the hero the wrong way.
 *
 * Using the real luminance is what makes the hero resolve correctly. All three
 * homepage slides carry a 34.9% black wash (`overlay_color: "#00000059"`):
 *
 *   0.34902 * 0 + (1 - 0.34902) * 0.214 = 0.139  <  0.179  ->  on-dark
 *
 * with no dependence on which CDN rendition loaded, how tall the header is, or
 * what the photograph actually contains. (Taking #808080 exactly: 0.2159 -
 * gives 0.141; the result has ample headroom either way.)
 */
const ASSUMED_MEDIA_LUMINANCE = 0.214;

/**
 * How bright a composited overlay has to be before dark ink goes over a photo.
 *
 * Deliberately far above LUMINANCE_FLIP_POINT, because the two failure modes
 * are not symmetric: white chrome stays legible over a mid-tone photograph,
 * dark ink does not. Bare photography (0.2140, which sits just above the WCAG
 * flip point) therefore still resolves on-dark; only a genuinely bright wash -
 * say a 60% white scrim, which composites to 0.686, flips it to on-light.
 * A section that wants the other answer uses the hint attribute in step (a).
 */
const MEDIA_LIGHT_FLIP_POINT = 0.5;

/* A section that starts exactly on the header's bottom edge should still be
   measurable, so the sample point sits this far below it. */
const BAND_BLEED = 2;

/* Media must span at least this fraction of the section width to count as a
   full-bleed backdrop. Keeps product-card images and the half-width
   media-with-content photo out of the hero branch. */
const FULL_BLEED_RATIO = 0.9;

/* Backstop only. The scroll listener on the resolved scroller is the primary
   trigger; this exists so a missed event can never persist for long. */
const SAFETY_INTERVAL_MS = 800;

/* ------------------------------------------------------------------ *
 * Hysteresis: how sure the module has to be before it moves
 *
 * Asymmetric on purpose. An unnecessary stint of WHITE chrome over a light
 * backdrop is ugly; an unnecessary stint of BLACK chrome over photography is
 * the bug the client reported. So on-light (dark ink) needs one more agreeing
 * sample and a longer settle than on-dark does.
 * ------------------------------------------------------------------ */

const CONFIRMATIONS_TO_DARK = 2; /* -> on-dark, i.e. light chrome */
const CONFIRMATIONS_TO_LIGHT = 3; /* -> on-light, i.e. dark chrome */

const SETTLE_MS_TO_DARK = 120;
const SETTLE_MS_TO_LIGHT = 200;

/* A disagreeing reading that has survived this long is committed on two
   confirmations even if the stricter count was never reached. Bounds how long a
   genuine section change can possibly take to land. */
const MAX_PENDING_MS = 600;

/* While a change is pending, poll at this rate. Scroll events stop the instant
   the user lifts their finger; without this the settle window would wait on the
   800ms backstop instead of closing on its own schedule. */
const PENDING_POLL_MS = 60;

/* A transition signal that never clears must not freeze the tone forever. */
const MAX_SKIP_MS = 2000;

/**
 * Attributes assets/slideshow.js stamps on <slideshow-component> while the
 * track is in motion.
 *
 * `transitioning`       Slideshow #onTransitionInit / #onTransitionEnd, wired to
 *                       Scroller's onScrollStart / onScrollEnd. Scroller raises
 *                       onScrollInit on the first scroll event of a burst -
 *                       before it checks its `ignore` flag, so this covers
 *                       instant and reduced-motion jumps as well as smooth
 *                       ones, and clears 50ms (debounced) after the last scroll
 *                       event. This is the primary signal.
 * `dragging`            pointer drag in progress.
 * `refreshing-timeline` the two-rAF flag raised after the wraparound DOM
 *                       reorder to force Safari to rebuild the view timeline.
 */
const SLIDESHOW_BUSY_ATTRIBUTES = ['transitioning', 'dragging', 'refreshing-timeline'];

/**
 * The slideshow's own chrome, none of which is ever the backdrop when a slide
 * is over the sample point. `slideshow-container` is the offender named in the
 * v6 note above: snippets/slideshow-styles.liquid paints it
 * `background-color: var(--color-background)`, opaque white on this page, and
 * it stacks BEHIND the slide media.
 */
const SLIDESHOW_CONTAINER_SELECTOR = 'slideshow-container, slideshow-slides, slideshow-component';

/* The scroll-snap track. Slides are `scroll-snap-align: center` and full-track
   width, so a settled slide sits flush with the track's leading edge. */
const SLIDE_TRACK_SELECTOR = 'slideshow-slides';

/**
 * Containers whose media is a BACKDROP rather than content.
 * `.slide__image-container`  blocks/_slide.liquid
 * `.hero__media-grid`        sections/hero.liquid
 * `.background-image-container`, `.video-background`  snippets/background-media.liquid
 * `.custom-section-background`                       snippets/section.liquid
 */
const HERO_BACKDROP_SELECTOR =
  '.slide__image-container, .hero__media-grid, .background-image-container, .video-background, .custom-section-background';

const MEDIA_SELECTOR = 'img, video, svg';

const SCROLLABLE_OVERFLOW = /^(auto|scroll|overlay)$/;

let rafId = 0;

/* Hysteresis state. `primed` records that at least one definite reading has
   been committed this session, until then a change lands immediately. */
let pendingTone = null;
let pendingCount = 0;
let pendingSince = 0;
let pendingTimer = 0;
let skipSince = 0;
let primed = false;

function now() {
  return typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now();
}

/* ------------------------------------------------------------------ *
 * Math
 * ------------------------------------------------------------------ */

function clamp(value, min, max) {
  return value < min ? min : value > max ? max : value;
}

function clamp01(value) {
  return clamp(value, 0, 1);
}

function srgbChannel(value) {
  const c = value / 255;
  return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

function relativeLuminance(r, g, b) {
  return 0.2126 * srgbChannel(r) + 0.7152 * srgbChannel(g) + 0.0722 * srgbChannel(b);
}

function toneForLuminance(luminance) {
  return luminance < LUMINANCE_FLIP_POINT ? TONE_DARK : TONE_LIGHT;
}

function intersectRects(a, b) {
  const left = Math.max(a.left, b.left);
  const top = Math.max(a.top, b.top);
  const right = Math.min(a.right, b.right);
  const bottom = Math.min(a.bottom, b.bottom);
  if (right <= left || bottom <= top) return null;
  return { left, top, right, bottom, width: right - left, height: bottom - top };
}

/* ------------------------------------------------------------------ *
 * Color parsing
 *
 * Two shapes have to be read. `getComputedStyle(el).backgroundColor` is always
 * an rgb()/rgba() serialization. A custom property is NOT normalised: it comes
 * back as the authored token stream, so `--color-background` arrives as the raw
 * hex Liquid wrote (#FFFFFF, #101010, #F5F4F2). Both are handled here; anything
 * else returns null and the caller walks further down, which is why an
 * unrecognised value can never strand the tone.
 * ------------------------------------------------------------------ */

/* Null-prototype: a lookup of "constructor" or "toString" must miss, not return
   an inherited function that would then read back as NaN channels. */
const NAMED_COLORS = Object.assign(Object.create(null), {
  transparent: { r: 0, g: 0, b: 0, a: 0 },
  white: { r: 255, g: 255, b: 255, a: 1 },
  black: { r: 0, g: 0, b: 0, a: 1 },
});

function parseHexColor(input) {
  let hex = input.slice(1);
  if (hex.length === 3 || hex.length === 4) {
    let expanded = '';
    for (let i = 0; i < hex.length; i++) expanded += hex[i] + hex[i];
    hex = expanded;
  }
  if (hex.length !== 6 && hex.length !== 8) return null;
  if (!/^[0-9a-f]+$/i.test(hex)) return null;
  return {
    r: parseInt(hex.slice(0, 2), 16),
    g: parseInt(hex.slice(2, 4), 16),
    b: parseInt(hex.slice(4, 6), 16),
    a: hex.length === 8 ? parseInt(hex.slice(6, 8), 16) / 255 : 1,
  };
}

function parseFunctionalColor(input) {
  const match = input.match(
    /rgba?\(\s*([\d.]+%?)[\s,]+([\d.]+%?)[\s,]+([\d.]+%?)(?:[\s,/]+([\d.]+%?))?\s*\)/i
  );
  if (!match) return null;

  const channel = (raw) => {
    const value = parseFloat(raw);
    if (!Number.isFinite(value)) return NaN;
    return raw.charAt(raw.length - 1) === '%' ? clamp((value / 100) * 255, 0, 255) : clamp(value, 0, 255);
  };

  let alpha = 1;
  if (match[4] !== undefined) {
    const value = parseFloat(match[4]);
    if (!Number.isFinite(value)) return null;
    alpha = clamp01(match[4].charAt(match[4].length - 1) === '%' ? value / 100 : value);
  }

  const color = { r: channel(match[1]), g: channel(match[2]), b: channel(match[3]), a: alpha };
  if (!Number.isFinite(color.r) || !Number.isFinite(color.g) || !Number.isFinite(color.b)) return null;
  return color;
}

function parseColor(value) {
  if (!value) return null;
  const input = String(value).trim();
  if (!input) return null;

  const named = NAMED_COLORS[input.toLowerCase()];
  if (named) return { r: named.r, g: named.g, b: named.b, a: named.a };

  if (input.charAt(0) === '#') return parseHexColor(input);
  return parseFunctionalColor(input);
}

/** Paint `layer` (semi-transparent) over an opaque {r,g,b} base. */
function compositeOver(base, layer) {
  return {
    r: layer.a * layer.r + (1 - layer.a) * base.r,
    g: layer.a * layer.g + (1 - layer.a) * base.g,
    b: layer.a * layer.b + (1 - layer.a) * base.b,
    a: 1,
  };
}

/** Paint `layer` over a base we only know the luminance of. */
function compositeLuminance(baseLuminance, layer) {
  return layer.a * relativeLuminance(layer.r, layer.g, layer.b) + (1 - layer.a) * baseLuminance;
}

/* ------------------------------------------------------------------ *
 * Scroll container resolution
 *
 * FINDING 1 above: the real scroller is div.page-wrapper on desktop and the
 * window below 990px. Walk up from the header and from #MainContent looking for
 * an element that actually scrolls, bind it, and re-run on resize (the media
 * query flips the scroller) and on shopify:section:load (the editor re-renders
 * the tree).
 *
 * A scroll event does not bubble, but it DOES run through the capture phase, so
 * the document-level capture listener below catches every nested scroller -
 * including the slideshow's scroll-snap track, as a second line of defence.
 * The explicit bindings remain the primary trigger.
 * ------------------------------------------------------------------ */

const boundScrollers = new WeakSet();

function bindScroller(el) {
  if (!el || boundScrollers.has(el)) return;
  boundScrollers.add(el);
  el.addEventListener('scroll', schedule, { passive: true });
}

function isScrollContainer(el) {
  if (!el || el.nodeType !== 1) return false;
  const overflowY = getComputedStyle(el).overflowY;
  if (!SCROLLABLE_OVERFLOW.test(overflowY)) return false;
  /* scrollHeight > clientHeight is the honest test, but a scroller whose images
     have not laid out yet does not overflow *yet*, binding it early costs
     nothing, so it is a tolerance rather than an exclusion. */
  return true;
}

function resolveScrollers() {
  const seeds = [
    document.getElementById(HEADER_ID),
    document.getElementById('MainContent'),
    document.querySelector('.page-wrapper'),
  ];

  for (const seed of seeds) {
    let node = seed;
    while (node && node.nodeType === 1 && node !== document.documentElement) {
      if (isScrollContainer(node)) bindScroller(node);
      node = node.parentElement;
    }
  }

  /* The slideshow track scrolls sideways without moving the page. */
  document.querySelectorAll('slideshow-slides').forEach(bindScroller);
}

/* ------------------------------------------------------------------ *
 * Element helpers
 * ------------------------------------------------------------------ */

function isRendered(el) {
  if (!el || el.nodeType !== 1) return false;
  if (el.getClientRects().length === 0) return false;
  if (typeof el.checkVisibility === 'function' && !el.checkVisibility()) return false;

  /* The slideshow marks off-screen slides aria-hidden. */
  if (el.closest('[aria-hidden="true"]')) return false;

  /* [hidden] normally means "not rendered", but assets/slideshow.js temporarily
     stamps `reveal` on a hidden slide while it is being shown. */
  const hiddenAncestor = el.closest('[hidden]');
  if (hiddenAncestor && !hiddenAncestor.hasAttribute('reveal')) return false;

  return true;
}

/**
 * The element's rect after clipping to its scroll track. <slideshow-slides> is
 * a scroll-snap track, so a slide parked further along it still reports real
 * coordinates even though none of it is painted. Clipping is what makes such a
 * slide measure as zero and hand over at the halfway point of a transition.
 */
function visibleRect(el) {
  const rect = el.getBoundingClientRect();
  const track = el.closest(SLIDE_TRACK_SELECTOR);
  if (!track) return rect;
  return intersectRects(rect, track.getBoundingClientRect());
}

function isPageSection(el) {
  if (!el || el.nodeType !== 1) return false;
  if (!el.classList.contains('shopify-section')) return false;
  if (el.classList.contains('header-section')) return false;
  return !el.closest('#header-group');
}

/**
 * The section sitting under the header. Primary: the nearest `.shopify-section`
 * ancestor of whatever `elementsFromPoint` reports at the sample point.
 * Fallback: the section whose bounding rect straddles the sample point, then
 * the section with the largest overlap with the header band.
 */
function resolveSection(stack, y, band) {
  for (const el of stack) {
    if (!el.closest) continue;
    let candidate = el.closest('.shopify-section');
    while (candidate && !isPageSection(candidate)) {
      const parent = candidate.parentElement;
      candidate = parent ? parent.closest('.shopify-section') : null;
    }
    if (candidate) return candidate;
  }

  let straddler = null;
  let widest = null;
  let widestArea = 0;

  for (const el of document.querySelectorAll('.shopify-section')) {
    if (!isPageSection(el)) continue;
    if (el.getClientRects().length === 0) continue;

    const rect = el.getBoundingClientRect();
    if (!straddler && rect.top <= y && rect.bottom > y) straddler = el;

    const hit = intersectRects(rect, band);
    if (hit) {
      const area = hit.width * hit.height;
      if (area > widestArea) {
        widestArea = area;
        widest = el;
      }
    }
  }

  return straddler || widest;
}

/* ------------------------------------------------------------------ *
 * Slideshow settle detection: the frames that must not be sampled
 * ------------------------------------------------------------------ */

/**
 * Is this slideshow mid-change?
 *
 * The attribute flags cover every animated and instant path. The `:empty`
 * lookup covers the one window they do not: on a wraparound, select() inserts
 * `document.createElement('slideshow-slide')` as a placeholder in the target
 * slide's DOM slot and only swaps it back a microtask later. A Liquid-rendered
 * slide always has element children and whitespace, so `:empty` isolates that
 * placeholder and nothing else.
 */
function slideshowBusy(component) {
  for (const name of SLIDESHOW_BUSY_ATTRIBUTES) {
    if (component.hasAttribute(name)) return true;
  }
  return component.querySelector('slideshow-slide:empty') !== null;
}

/**
 * Is the slide containing `node` off its snap position?
 *
 * The documented fallback signal: a settled full-track slide sits flush with
 * the track's leading edge, so a non-zero offset means the track is still
 * moving. Scoped to slides that fill the track: a multi-up product carousel
 * legitimately parks its slides anywhere.
 */
function slideMisaligned(node) {
  if (!node || typeof node.closest !== 'function') return false;

  const slide = node.closest('slideshow-slide');
  if (!slide) return false;

  const track = slide.closest(SLIDE_TRACK_SELECTOR);
  if (!track) return false;

  const slideRect = slide.getBoundingClientRect();
  const trackRect = track.getBoundingClientRect();
  if (trackRect.width <= 0 || slideRect.width <= 0) return false;
  if (slideRect.width < trackRect.width * FULL_BLEED_RATIO) return false;

  const tolerance = Math.max(2, trackRect.width * 0.02);
  return Math.abs(slideRect.left - trackRect.left) > tolerance;
}

/**
 * True when the sample point sits inside a slideshow that is mid-transition.
 * `elementsFromPoint` reports innermost first, so the first entry that has a
 * <slideshow-component> ancestor identifies the slideshow actually under the
 * header; anything further down the stack is its ancestor chain.
 */
function slideshowUnsettled(stack) {
  for (const el of stack) {
    if (!el.closest) continue;
    const component = el.closest('slideshow-component');
    if (!component) continue;
    if (slideshowBusy(component)) return true;
    return slideMisaligned(el);
  }
  return false;
}

/**
 * Does a SLIDE's backdrop media cover the sample point?
 *
 * When it does, every slideshow container below it in the stack is behind that
 * media and none of them may stand in for it, requirement (2) in the v6 note.
 *
 * Deliberately narrow on both sides. It requires a HERO_BACKDROP_SELECTOR
 * container *inside a slideshow-slide*, which on this theme means
 * `.slide__image-container` from blocks/_slide.liquid and nothing else. So:
 *
 *   - a product carousel's slides carry no such container, so its
 *     `slideshow-container` keeps being read normally and the white products
 *     section still resolves;
 *   - a plain full-bleed section backdrop (`.background-image-container`,
 *     `.custom-section-background`) is untouched and keeps v5's token fallback,
 *     so nothing that used to answer can start returning null.
 *
 * And where it IS true, heroBackdropTone() is guaranteed to answer for a
 * settled slide, `.slide__image-container` is `position: absolute; width: 100%;
 * height: 100%` of a full-track slide and always holds an img, video or
 * placeholder svg, so the null branch below is reachable only on frames that
 * are already being skipped as mid-transition. There is no way to sit in it.
 */
function slideBackdropCoversPoint(stack) {
  for (const el of stack) {
    if (!el.closest) continue;
    const backdrop = el.closest(HERO_BACKDROP_SELECTOR);
    if (backdrop && backdrop.closest('slideshow-slide')) return true;
  }
  return false;
}

/* ------------------------------------------------------------------ *
 * (a) Explicit hint: the escape hatch
 * ------------------------------------------------------------------ */

function normaliseTone(value) {
  if (!value) return null;
  const tone = String(value).trim().toLowerCase();
  if (tone === 'dark' || tone === TONE_DARK) return TONE_DARK;
  if (tone === 'light' || tone === TONE_LIGHT) return TONE_LIGHT;
  return null;
}

function hintFromChain(node, header) {
  let el = node;
  while (el && el.nodeType === 1) {
    if (el !== header && el.hasAttribute(HINT_ATTRIBUTE)) {
      const tone = normaliseTone(el.getAttribute(HINT_ATTRIBUTE));
      if (tone) return tone;
    }
    el = el.parentElement;
  }
  return null;
}

function hintTone(stack, section, header) {
  if (stack.length > 0) {
    const tone = hintFromChain(stack[0], header);
    if (tone) return tone;
  }
  if (section) return hintFromChain(section, header);
  return null;
}

/* ------------------------------------------------------------------ *
 * (b) Hero / background media
 *
 * The overlay is located STRUCTURALLY, never by hit testing: `.overlay` carries
 * `pointer-events: none` (snippets/overlay.liquid), so elementsFromPoint can
 * never return it. That single fact is what biased every previous sampler ~35%
 * toward "on-light" on the overlaid hero.
 * ------------------------------------------------------------------ */

/**
 * The effective color a gradient overlay contributes across `band`.
 *
 * `background-color` computes to rgba(0,0,0,0) for `.overlay--gradient`, so a
 * naive read contributes nothing. `backgroundImage` is fully var()-substituted
 * by the time it is computed, so the resolved stops can be read straight out of
 * it. Alpha ramps linearly along the gradient line, so its mean across the band
 * equals its value at the band's midpoint.
 */
function gradientLayer(backgroundImage, rect, band) {
  if (!backgroundImage || backgroundImage.indexOf('gradient') === -1) return null;
  if (rect.height <= 0) return null;

  const stops = [];
  const pattern = /rgba?\([^)]*\)/g;
  let match;
  while ((match = pattern.exec(backgroundImage)) !== null) {
    const color = parseColor(match[0]);
    if (color) stops.push(color);
  }
  if (stops.length === 0) return null;
  if (stops.length === 1) return stops[0];

  const first = stops[0];
  const last = stops[stops.length - 1];

  /* Where the band sits inside the overlay box: 0 = top edge, 1 = bottom. */
  const t0 = clamp01((Math.max(band.top, rect.top) - rect.top) / rect.height);
  const t1 = clamp01((Math.min(band.bottom, rect.bottom) - rect.top) / rect.height);
  const t = (t0 + t1) / 2;

  /* `to top` puts the FIRST stop at the bottom, so the top edge is the LAST
     stop. `to bottom` (which browsers omit, being the default) is the reverse.
     Angled gradients have no vertical ramp we can trust, so use the midpoint. */
  let k; /* 0 -> first stop, 1 -> last stop */
  if (/to\s+top\b/.test(backgroundImage)) {
    k = 1 - t;
  } else if (/to\s+bottom\b/.test(backgroundImage)) {
    k = t;
  } else if (/linear-gradient\(\s*rgba?\(/.test(backgroundImage)) {
    k = t; /* implicit `to bottom` */
  } else {
    k = 0.5;
  }

  return {
    r: first.r + (last.r - first.r) * k,
    g: first.g + (last.g - first.g) * k,
    b: first.b + (last.b - first.b) * k,
    a: first.a + (last.a - first.a) * k,
  };
}

function overlayLayer(overlayEl, rect, band) {
  const cs = getComputedStyle(overlayEl);

  /* Solid overlays say it all in `background-color`. This is the hero case -
     #00000059 computes to rgba(0, 0, 0, 0.34902). */
  const solid = parseColor(cs.backgroundColor);
  if (solid && solid.a > 0) return solid;

  return gradientLayer(cs.backgroundImage, rect, band);
}

/**
 * Overlay colors painting over `mediaEl` within `band`, in DOM (paint) order,
 * bottom-most first. Scope is the enclosing slide or backdrop container so a
 * neighbouring slide's overlay can never contaminate this measurement.
 */
function overlaysOver(mediaEl, band) {
  const layers = [];
  const boundary =
    mediaEl.closest('slideshow-slide') || mediaEl.closest(HERO_BACKDROP_SELECTOR) || mediaEl.parentElement;
  if (!boundary) return layers;

  for (const el of boundary.querySelectorAll('.overlay')) {
    if (el === mediaEl || el.contains(mediaEl)) continue;
    if (!isRendered(el)) continue;
    const rect = el.getBoundingClientRect();
    if (!intersectRects(rect, band)) continue;
    const layer = overlayLayer(el, rect, band);
    if (layer && layer.a > 0) layers.push(layer);
  }

  return layers;
}

/**
 * Tone from full-bleed backdrop media covering the sample point, or null when
 * this section has none (which sends the caller on to the surface-color branch).
 */
function heroBackdropTone(section, band) {
  if (!section) return null;

  const sectionRect = section.getBoundingClientRect();
  if (sectionRect.width <= 0) return null;
  const minWidth = sectionRect.width * FULL_BLEED_RATIO;

  let best = null;
  let bestArea = 0;

  for (const el of section.querySelectorAll(MEDIA_SELECTOR)) {
    if (!el.closest(HERO_BACKDROP_SELECTOR)) continue;
    if (!isRendered(el)) continue;

    /* A slide still travelling along the track is not a backdrop yet. */
    if (slideMisaligned(el)) continue;

    const rect = visibleRect(el);
    if (!rect || rect.width < minWidth) continue;

    const hit = intersectRects(rect, band);
    if (!hit) continue;

    const area = hit.width * hit.height;
    if (area > bestArea) {
      bestArea = area;
      best = el;
    }
  }

  if (!best) return null;

  let luminance = ASSUMED_MEDIA_LUMINANCE;
  for (const layer of overlaysOver(best, band)) {
    luminance = compositeLuminance(luminance, layer);
  }

  /* Darkened past the WCAG flip point: unambiguously dark. */
  if (luminance < LUMINANCE_FLIP_POINT) return TONE_DARK;
  /* A genuinely bright wash over the photo: dark ink is safe. */
  if (luminance > MEDIA_LIGHT_FLIP_POINT) return TONE_LIGHT;
  /* Bare or lightly-tinted photography: white chrome is the survivable read. */
  return TONE_DARK;
}

/* ------------------------------------------------------------------ *
 * (c) The section's own surface color
 * ------------------------------------------------------------------ */

/**
 * The resolved `--color-background` on `el`.
 *
 * Horizon publishes this on every `.color-custom-*` scope
 * (snippets/contrast-override.liquid) and on `:root`
 * (snippets/color-palette.liquid), and custom properties inherit, so reading
 * it off the deepest element under the header yields that section's surface,
 * and yields the page background for sections that set none. This is the read
 * that makes the transparent `.section` wrapper irrelevant.
 */
function backgroundToken(el) {
  if (!el || el.nodeType !== 1) return null;
  const color = parseColor(getComputedStyle(el).getPropertyValue('--color-background'));
  return color && color.a > 0 ? color : null;
}

/**
 * Returns a tone, or null meaning "no trustworthy answer", which the caller
 * resolves by KEEPING the tone already on screen.
 */
function surfaceTone(stack, section) {
  const layers = []; /* semi-transparent, top-most first */
  let base = null;

  /* Slide media over the point means every slideshow container below it is a
     backdrop it is hiding, not a backdrop we may read. */
  const masked = slideBackdropCoversPoint(stack);

  for (const el of stack) {
    /* STOP BEFORE <body>. It is opaque: `body { background-color: var(--color-background) }`
       (snippets/color-palette.liquid), so reading it here would mask every
       section that paints its surface on the negatively-stacked
       `.section-background` sibling the hit stack skips, which is exactly the
       near-black Academy panel. It is still reachable, but only as the LAST
       resort below, after the section's own token has had its say. */
    if (el === document.body || el === document.documentElement) break;

    const skip = masked && typeof el.matches === 'function' && el.matches(SLIDESHOW_CONTAINER_SELECTOR);

    if (!skip) {
      const color = parseColor(getComputedStyle(el).backgroundColor);
      if (color && color.a > 0) {
        if (color.a >= 0.99) {
          base = color;
          break;
        }
        layers.push(color);
      }
    }

    /* Nothing below the section belongs to the section. */
    if (section && el === section) break;
  }

  if (!base) {
    /* Media covers the point but the hero branch declined to answer, so this
       frame simply has no honest reading. Falling through to the section token
       would land on the same opaque white the container was skipped for. */
    if (masked) return null;

    base = backgroundToken(stack.length > 0 ? stack[0] : null);
    if (!base) base = backgroundToken(section);
    if (!base) base = parseColor(getComputedStyle(document.body).backgroundColor);
  }

  /* No white default any more: an unknown surface keeps the current tone
     rather than volunteering dark chrome. */
  if (!base || base.a <= 0) return null;

  /* Composite bottom-up: the last entry collected is the closest to the base. */
  let composited = base;
  for (let i = layers.length - 1; i >= 0; i--) {
    composited = compositeOver(composited, layers[i]);
  }

  return toneForLuminance(relativeLuminance(composited.r, composited.g, composited.b));
}

/* ------------------------------------------------------------------ *
 * (d) Final fallback: the page background. Always answers.
 * ------------------------------------------------------------------ */

function pageTone() {
  let base = parseColor(getComputedStyle(document.body).backgroundColor);
  if (!base || base.a <= 0) base = backgroundToken(document.documentElement);
  if (!base || base.a <= 0) base = { r: 255, g: 255, b: 255, a: 1 };
  return toneForLuminance(relativeLuminance(base.r, base.g, base.b));
}

/* ------------------------------------------------------------------ *
 * Writing the attribute: hysteresis lives here
 * ------------------------------------------------------------------ */

function setTone(header, tone) {
  if (header.getAttribute(TONE_ATTRIBUTE) !== tone) {
    header.setAttribute(TONE_ATTRIBUTE, tone);
  }
}

function clearPending() {
  pendingTone = null;
  pendingCount = 0;
  pendingSince = 0;
  if (pendingTimer) {
    clearTimeout(pendingTimer);
    pendingTimer = 0;
  }
}

function armPendingPoll() {
  if (pendingTimer) return;
  pendingTimer = setTimeout(() => {
    pendingTimer = 0;
    schedule();
  }, PENDING_POLL_MS);
}

/**
 * Commit a reading, subject to hysteresis.
 *
 * - a reading equal to the attribute already on the element is a no-op;
 * - a null reading keeps the current tone, and only seeds pageTone() when
 *   there is no current tone at all;
 * - a differing reading has to be seen CONFIRMATIONS_TO_* times running AND
 *   survive SETTLE_MS_TO_* before it is written, with MAX_PENDING_MS as the
 *   upper bound so a genuine section change can never hang.
 */
function commitTone(header, tone) {
  const current = normaliseTone(header.getAttribute(TONE_ATTRIBUTE));

  if (!tone) {
    if (!current) setTone(header, pageTone());
    return;
  }

  const first = !primed;
  primed = true;

  if (tone === current) {
    clearPending();
    return;
  }

  /* Nothing to keep stable yet: land the first definite reading immediately,
     otherwise the settle window itself shows up as a load-time flash. */
  if (!current || first) {
    clearPending();
    setTone(header, tone);
    return;
  }

  const required = tone === TONE_LIGHT ? CONFIRMATIONS_TO_LIGHT : CONFIRMATIONS_TO_DARK;
  const settle = tone === TONE_LIGHT ? SETTLE_MS_TO_LIGHT : SETTLE_MS_TO_DARK;
  const stamp = now();

  if (pendingTone !== tone) {
    pendingTone = tone;
    pendingCount = 1;
    pendingSince = stamp;
  } else {
    pendingCount += 1;
  }

  const elapsed = stamp - pendingSince;

  if ((pendingCount >= required && elapsed >= settle) || (pendingCount >= 2 && elapsed >= MAX_PENDING_MS)) {
    clearPending();
    setTone(header, tone);
    return;
  }

  armPendingPoll();
}

/* ------------------------------------------------------------------ *
 * Main pass
 * ------------------------------------------------------------------ */

/**
 * One reading. Returns a tone, UNSETTLED (mid slide transition: do not use
 * this frame at all), or null (measured, but not trustworthy).
 *
 * @param {Element} header
 * @param {boolean} [allowUnsettled] skip the transition guard, used only by the
 *   MAX_SKIP_MS escape so a stuck signal cannot freeze the chrome.
 */
function measure(header, allowUnsettled) {
  const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
  const rect = header.getBoundingClientRect();

  /* No measurable header box: still produce a definite tone rather than
     leaving the previous value latched. */
  if (rect.width <= 0 || rect.height <= 0 || viewportWidth <= 0 || viewportHeight <= 0) {
    return pageTone();
  }

  /* THE BAND: the header's own footprint, clamped to the viewport, nudged just
     past its bottom edge. Every measurement below is taken from this strip. */
  const band = {
    left: Math.max(rect.left, 0),
    top: Math.max(rect.top, 0),
    right: Math.min(rect.right, viewportWidth),
    bottom: Math.min(rect.bottom + BAND_BLEED, viewportHeight),
  };
  band.width = band.right - band.left;
  band.height = band.bottom - band.top;

  const x = clamp(rect.left + rect.width / 2, 0, viewportWidth - 1);
  const y = clamp(rect.bottom + BAND_BLEED, 0, viewportHeight - 1);

  const stack = (document.elementsFromPoint(x, y) || []).filter((el) => el && !header.contains(el));

  /* (1) IGNORE TRANSITION FRAMES. Nothing measured here is stable, and the one
     thing that reliably IS under the point is the container's opaque white. */
  if (!allowUnsettled && slideshowUnsettled(stack)) return UNSETTLED;

  const section = resolveSection(stack, y, band);

  const hinted = hintTone(stack, section, header);
  if (hinted) return hinted;

  if (band.width > 0 && band.height > 0) {
    const backdrop = heroBackdropTone(section, band);
    if (backdrop) return backdrop;
  }

  return surfaceTone(stack, section);
}

function evaluate() {
  const header = document.getElementById(HEADER_ID);
  if (!header) return;

  const reading = measure(header, false);

  if (reading === UNSETTLED) {
    const stamp = now();
    if (skipSince === 0) skipSince = stamp;

    /* Normal case: the transition is short, hold the tone and wait it out. */
    if (stamp - skipSince < MAX_SKIP_MS) return;

    /* A busy flag that never cleared. Measure anyway rather than stay frozen. */
    skipSince = 0;
    commitTone(header, measure(header, true));
    return;
  }

  skipSince = 0;
  commitTone(header, reading);
}

/**
 * A throw inside one pass must never strand the chrome. It must not blank it
 * either: keep whatever is on screen and only seed a value when there is none.
 */
function sample() {
  try {
    evaluate();
  } catch (error) {
    try {
      const header = document.getElementById(HEADER_ID);
      if (header && !normaliseTone(header.getAttribute(TONE_ATTRIBUTE))) {
        setTone(header, pageTone());
      }
    } catch (ignored) {
      /* nothing left to do */
    }
  }
}

function schedule() {
  if (rafId) return;
  rafId = requestAnimationFrame(() => {
    rafId = 0;
    sample();
  });
}

/* ------------------------------------------------------------------ *
 * Wiring
 * ------------------------------------------------------------------ */

function refresh() {
  resolveScrollers();
  observeSlideshows();
  schedule();
}

/* Primary trigger: the real scroller(s). */
resolveScrollers();

/* Catch-all: scroll does not bubble, but it does capture. */
document.addEventListener('scroll', schedule, { passive: true, capture: true });
window.addEventListener('scroll', schedule, { passive: true });

window.addEventListener('resize', refresh);
window.addEventListener('orientationchange', refresh);
window.addEventListener('load', refresh);
window.addEventListener('pageshow', refresh);
window.addEventListener('pagereveal', refresh);

document.addEventListener('shopify:section:load', refresh);
document.addEventListener('shopify:section:unload', refresh);
document.addEventListener('shopify:section:reorder', refresh);
document.addEventListener('shopify:section:select', schedule);

/* Slide transitions happen without any scroll: observe slideshow attribute
   churn. Under `prefers-reduced-motion` the slideshow jumps instantly, so the
   aria-hidden mutation is the only signal, which is precisely why this
   observer, and not a transition hook, is what watches it.
   `transitioning` is in the filter for the opposite reason: its REMOVAL is the
   settle edge, and sampling on it is what makes the new slide's tone confirm
   immediately instead of waiting on the 800ms backstop. */
const slideObserver = new MutationObserver(schedule);
const observedSlideshows = new WeakSet();

const SLIDESHOW_OBSERVED_ATTRIBUTES = [
  'aria-hidden',
  'class',
  'selected',
  'data-selected',
  'aria-current',
  'hidden',
  'reveal',
  'transitioning',
  'dragging',
  'refreshing-timeline',
  'in-viewport',
];

function observeSlideshows() {
  document.querySelectorAll('slideshow-component, slideshow-slides, slideshow-slide').forEach((el) => {
    if (observedSlideshows.has(el)) return;
    observedSlideshows.add(el);
    slideObserver.observe(el, {
      attributes: true,
      attributeFilter: SLIDESHOW_OBSERVED_ATTRIBUTES,
      /* The wraparound placeholder is inserted into and removed from the track,
         and neither move touches an attribute. */
      childList: el.matches(SLIDE_TRACK_SELECTOR),
    });
  });
}

observeSlideshows();

setInterval(schedule, SAFETY_INTERVAL_MS);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', refresh, { once: true });
} else {
  schedule();
}
